/**
 * V13.21 (Itération C) — Admin : gestion & fusion des Athlètes du Pacifique
 *
 * Route (protégée admin/copf) : /admin/pacific-athletes
 *
 * Permet :
 *   - lister/filtrer les PacificAthletes
 *   - sélectionner 2 fiches candidats pour fusion (target ← source)
 *   - déclencher la fusion (transfert participants + union aliases/disciplines +
 *     soft-delete source)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Globe, Search, Flag, GitMerge, ArrowRight, X, ArrowRightLeft, ExternalLink,
  Loader2, Info, Trophy, AlertTriangle,
} from "lucide-react";
import { showError } from "@/lib/errors";

function formatDiscipline(slug) {
  if (!slug) return "—";
  return String(slug).split("-").filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function PARow({ pa, selectedRole, onToggle, onClear }) {
  const isSource = selectedRole === "source";
  const isTarget = selectedRole === "target";
  const highlighted = isSource || isTarget;
  return (
    <tr
      className={`border-t transition-colors ${
        isSource ? "bg-red-50/70 dark:bg-red-950/20"
          : isTarget ? "bg-emerald-50/70 dark:bg-emerald-950/20"
          : "hover:bg-slate-50 dark:hover:bg-slate-900/40"
      }`}
      data-testid={`admin-pa-row-${pa.id}`}
    >
      <td className="p-2">
        <Link to={`/pacific-athletes/${pa.id}`} target="_blank" className="font-semibold hover:text-[hsl(var(--aito-lagoon))] hover:underline text-sm">
          {pa.display_name}
        </Link>
        {(pa.aliases || []).length > 1 && (
          <div className="text-[10px] text-muted-foreground truncate max-w-[280px]">
            alias : {pa.aliases.filter(a => a !== pa.display_name).slice(0, 3).join(", ")}
            {pa.aliases.length > 4 && "…"}
          </div>
        )}
      </td>
      <td className="p-2">
        <Badge variant="outline" className="text-xs">
          <Flag className="h-2.5 w-2.5 mr-1" />
          {pa.delegation_code}
        </Badge>
      </td>
      <td className="p-2 text-xs text-muted-foreground truncate max-w-[200px]">
        {(pa.disciplines || []).map(formatDiscipline).join(", ") || "—"}
      </td>
      <td className="p-2 text-center font-mono text-xs">{pa.participations_count || 0}</td>
      <td className="p-2 text-right">
        {highlighted ? (
          <div className="flex items-center justify-end gap-1">
            <Badge className={isSource ? "bg-red-500 text-white" : "bg-emerald-500 text-white"}>
              {isSource ? "Source (à fusionner)" : "Cible (conservée)"}
            </Badge>
            <Button size="sm" variant="ghost" onClick={() => onClear(selectedRole)} className="h-6 w-6 p-0" data-testid={`admin-pa-clear-${pa.id}`}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1">
            <Button size="sm" variant="outline" onClick={() => onToggle("source", pa)} className="h-7 text-[10px]" data-testid={`admin-pa-pick-source-${pa.id}`}>
              Source →
            </Button>
            <Button size="sm" variant="outline" onClick={() => onToggle("target", pa)} className="h-7 text-[10px]" data-testid={`admin-pa-pick-target-${pa.id}`}>
              ← Cible
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminPacificAthletesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [delegation, setDelegation] = useState("");
  const [source, setSource] = useState(null);
  const [target, setTarget] = useState(null);
  const [merging, setMerging] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (delegation) params.delegation = delegation;
    api
      .get("/admin/pacific-athletes", { params })
      .then((r) => {
        setItems(r.data?.items || []);
        setTotal(r.data?.count || 0);
      })
      .catch((e) => {
        showError(e, { prefix: "Chargement :" });
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, delegation]);

  const pickRow = (role, pa) => {
    if (role === "source") {
      if (target && target.id === pa.id) setTarget(null);
      setSource(pa);
    } else {
      if (source && source.id === pa.id) setSource(null);
      setTarget(pa);
    }
  };
  const clearRole = (role) => {
    if (role === "source") setSource(null);
    else setTarget(null);
  };

  const canMerge = source && target && source.id !== target.id;
  const sameDelegation = source && target && (source.delegation_code || "").toLowerCase() === (target.delegation_code || "").toLowerCase();

  const roleFor = (pa) => {
    if (source && source.id === pa.id) return "source";
    if (target && target.id === pa.id) return "target";
    return null;
  };

  const doMerge = async () => {
    if (!canMerge) return;
    if (!sameDelegation) {
      toast.error("Impossible : délégations différentes. Corrigez les fiches d'abord.");
      return;
    }
    const msg = `Fusionner définitivement :\n\n  ⚠ SOURCE : ${source.display_name} (${source.delegation_code})\n  ✓ CIBLE  : ${target.display_name} (${target.delegation_code})\n\nToutes les participations de la source seront transférées vers la cible.\nLes aliases et disciplines seront fusionnés.\nLa source sera archivée (soft-delete).\n\nContinuer ?`;
    if (!window.confirm(msg)) return;
    setMerging(true);
    try {
      const { data } = await api.post(
        `/admin/pacific-athletes/${source.id}/merge-into/${target.id}`
      );
      toast.success(
        `Fusion réussie : ${data.participants_transferred} participation(s) transférée(s), ${data.target_aliases_count} alias au total.`
      );
      setSource(null);
      setTarget(null);
      await load();
    } catch (e) {
      showError(e, { prefix: "Fusion :" });
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6" data-testid="admin-pacific-athletes-page">
      {/* Header */}
      <div className="mb-5 flex items-start gap-3">
        <div className="rounded-full bg-[hsl(var(--aito-lagoon))]/10 border border-[hsl(var(--aito-lagoon))]/25 h-11 w-11 flex items-center justify-center shrink-0">
          <Globe className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-bold text-2xl leading-tight">Athlètes du Pacifique — Administration</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Consolider les identités documentaires étrangères issues des imports historiques.
            Utilisez la fusion pour regrouper deux fiches d'une même personne (variantes de nom).
          </p>
        </div>
      </div>

      {/* Filtres */}
      <Card className="p-3 mb-3" data-testid="admin-pa-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative min-w-[220px] flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, alias…" className="pl-8 h-9" data-testid="admin-pa-filter-q" />
          </div>
          <Input value={delegation} onChange={(e) => setDelegation(e.target.value)} placeholder="Délégation (ex: Fidji)" className="h-9 max-w-[220px]" data-testid="admin-pa-filter-delegation" />
          <span className="text-xs text-muted-foreground ml-auto" data-testid="admin-pa-count">
            {loading ? "Chargement…" : `${total.toLocaleString("fr-FR")} fiche${total > 1 ? "s" : ""}`}
          </span>
        </div>
      </Card>

      {/* Bandeau merge (barre flottante quand 1 ou 2 fiches sélectionnées) */}
      {(source || target) && (
        <Card className="p-3 mb-3 border-2 border-[hsl(var(--aito-lagoon))]" data-testid="admin-pa-merge-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[260px]">
              <Badge className="bg-red-500 text-white">Source</Badge>
              {source ? (
                <span className="font-semibold text-sm">{source.display_name} ({source.delegation_code})</span>
              ) : (
                <span className="text-xs text-muted-foreground italic">non sélectionnée</span>
              )}
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex items-center gap-2 flex-1 min-w-[260px]">
              <Badge className="bg-emerald-500 text-white">Cible</Badge>
              {target ? (
                <span className="font-semibold text-sm">{target.display_name} ({target.delegation_code})</span>
              ) : (
                <span className="text-xs text-muted-foreground italic">non sélectionnée</span>
              )}
            </div>
            <Button
              onClick={doMerge}
              disabled={!canMerge || merging}
              className="bg-[hsl(var(--aito-lagoon))] text-white hover:bg-[hsl(var(--aito-lagoon-2))]"
              data-testid="admin-pa-merge-execute"
            >
              {merging ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <GitMerge className="h-4 w-4 mr-1.5" />}
              Fusionner
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setSource(null); setTarget(null); }} data-testid="admin-pa-merge-reset">
              Réinitialiser
            </Button>
          </div>
          {source && target && !sameDelegation && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded p-2" data-testid="admin-pa-merge-warn">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                <strong>Délégations différentes</strong> — fusion refusée pour éviter d'écraser une identité distincte. Corrigez la délégation d'une des fiches d'abord.
              </span>
            </div>
          )}
        </Card>
      )}

      {/* Liste */}
      <Card className="p-0 overflow-hidden" data-testid="admin-pa-list">
        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Globe className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Aucun athlète pour ce filtre.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="p-2">Athlète</th>
                  <th className="p-2">Délégation</th>
                  <th className="p-2">Disciplines</th>
                  <th className="p-2 text-center">Participations</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((pa) => (
                  <PARow key={pa.id} pa={pa} selectedRole={roleFor(pa)} onToggle={pickRow} onClear={clearRole} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="mt-4 text-[11px] italic text-muted-foreground flex items-center gap-1.5">
        <Info className="h-3 w-3" />
        La fusion est audit-safe : la source est <strong>archivée</strong> (soft-delete + <code>merged_into</code> traceable), jamais supprimée physiquement.
      </p>
    </div>
  );
}
